/*
 OK FBI — Custom Case Builder
 Host-only comprehensive form to create/edit custom cases.
 Fully wired to backend /api/custom-cases endpoints.
 Preserves all existing app features — the resulting case behaves exactly like a built-in one.
*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { useAuth } from "@/pages/AuthPage";
import { assetUrl } from "@/contexts/SessionContext";
import { sfx } from "@/lib/sounds";
import { toast } from "sonner";
import {
  Plus, Trash2, Copy, Eye, EyeOff, Save, Upload, ArrowRight, ArrowLeft, X, Check, Pencil,
  FileText, User, Users, Camera, Fingerprint as FpIcon, Dna, Volume2, PhoneCall, Beaker, MapPin, Clock, Sparkles, Layout, ChevronDown, AlertTriangle, Lock, Unlock,
} from "lucide-react";

// ============ helpers ============
const uid = (p = "X") => `${p}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

const SECTIONS = [
  { key: "basics",      label: "المعلومات الأساسية", icon: FileText },
  { key: "media",       label: "الغلاف والمشهد",     icon: Layout },
  { key: "roster",      label: "سجل الشخصيات",        icon: Users },
  { key: "suspects",    label: "المشتبه بهم",         icon: User },
  { key: "evidence",    label: "الأدلة",              icon: FileText },
  { key: "cctv",        label: "كاميرات المراقبة",    icon: Camera },
  { key: "forensics",   label: "المختبر الجنائي",     icon: FpIcon },
  { key: "phone_docs",  label: "الهاتف والوثائق",    icon: PhoneCall },
  { key: "scene",       label: "مسرح الجريمة",        icon: MapPin },
  { key: "timeline",    label: "الجدول الزمني",       icon: Clock },
  { key: "interr",      label: "الاستجواب",           icon: Volume2 },
  { key: "solution",    label: "الحل السري", icon: Sparkles },
];

// ============ Fictional-Content Safety Notice (Case Builder) ============
// Explicit reminder for OWNER/CASE_EDITOR that every case must be strictly fictional.
// Purely informational — does NOT block or gate case creation. Compact, dismissible per session.
function CaseFictionNotice() {
  const KEY = "okfbi_case_fiction_seen";
  const [dismissed, setDismissed] = useState(() => {
    try { return typeof sessionStorage !== "undefined" && sessionStorage.getItem(KEY) === "1"; }
    catch { return false; }
  });
  if (dismissed) return null;
  const close = () => {
    try { sessionStorage.setItem(KEY, "1"); } catch {}
    setDismissed(true);
  };
  return (
    <div
      data-testid="case-builder-fiction-notice"
      className="mb-5 border border-amber-800/60 bg-amber-950/25 text-amber-100 font-cairo relative"
      style={{ borderRadius: 2 }}
    >
      <div className="p-4 pl-10">
        <div className="flex items-start gap-2 mb-2">
          <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
          <div className="text-[13px] font-black text-amber-200 tracking-wider">تنبيه: محتوى القضايا خيالي فقط</div>
        </div>
        <div className="text-[11px] leading-relaxed text-amber-100/85">
          كل قضية داخل OK FBI يجب أن تكون خيالية بالكامل. عند بناء أي قضية استخدم:
          أسماء ومشتبه بهم وضحايا وشهود وشخصيات خيالية، صور مُولّدة بالذكاء الاصطناعي أو مصمَّمة خصيصاً للعبة،
          عناوين وأرقام هواتف ولوحات مركبات وشركات وأرقام ملفات وأدلة ومحادثات خيالية.
          يُمنع استخدام بيانات أو صور أشخاص حقيقيين لإنشاء مشتبه بهم أو ضحايا، ويُمنع اتهام شخص حقيقي داخل اللعبة.
        </div>
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="إغلاق التنبيه"
        data-testid="case-builder-fiction-dismiss"
        className="absolute top-2 left-2 w-6 h-6 flex items-center justify-center text-amber-300/70 hover:text-amber-200 transition-colors"
      >
        <X size={14} />
      </button>
    </div>
  );
}


// ============ list route ============
export function CaseBuilderList() {
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const [cases, setCases] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAI, setShowAI] = useState(false);

  const targetCase = useMemo(
    () => (confirmDel ? cases.find((c) => c.id === confirmDel) || null : null),
    [cases, confirmDel]
  );

  useEffect(() => {
    if (!confirmDel) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !deleting) setConfirmDel(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [confirmDel, deleting]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/custom-cases");
      setCases(data || []);
    } catch {
      setCases([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createBlank = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/custom-cases", { title: "قضية جديدة", code: `OKF-${Date.now().toString().slice(-3)}` });
      sfx.notification(); nav(`/host/case-builder/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إنشاء القضية");
    } finally { setBusy(false); }
  };

  const dupBuiltin = async (source_id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/custom-cases/new/duplicate-builtin/${source_id}`);
      sfx.notification(); nav(`/host/case-builder/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تكرار القضية");
    } finally { setBusy(false); }
  };

  const dupCustom = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/custom-cases/${id}/duplicate`);
      toast.success("تم إنشاء نسخة"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تكرار القضية");
    } finally { setBusy(false); }
  };

  const publish = async (id) => {
    if (!window.confirm("نشر القضية؟ ستظهر للمستخدمين المسموح لهم فوراً بدون إعادة نشر الموقع.")) return;
    try {
      await api.post(`/custom-cases/${id}/publish`);
      toast.success("✓ تم نشر القضية"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل نشر القضية");
    }
  };
  const unpublish = async (id) => {
    if (!window.confirm("إلغاء نشر القضية؟ ستعود مسودة وتختفي من المستخدمين. لن تُحذَف بياناتها.")) return;
    try {
      await api.post(`/custom-cases/${id}/unpublish`);
      toast.success("تم إلغاء النشر — عادت للمسودة"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إلغاء النشر");
    }
  };
  const disable = async (id) => {
    try {
      await api.post(`/custom-cases/${id}/disable`);
      toast.success("تم تعطيل القضية"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تعطيل القضية");
    }
  };
  const del = async (id) => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/custom-cases/${id}`);
      toast.success("تم الحذف");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل حذف القضية");
    } finally {
      setDeleting(false);
    }
  };

  const toggleLock = async (id) => {
    try {
      const { data } = await api.post(`/cases/${id}/toggle-lock`);
      toast.success(data.is_locked ? "🔒 تم قفل القضية — تظهر كـ 'قريباً' للمستخدمين" : "🔓 تم فتح القضية وأصبحت متاحة للتحقيق");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تغيير حالة القفل");
    }
  };

  const [builtins] = useState(["case-001", "case-002", "case-003", "case-004", "case-005"]);
  return (
    <Shell>
      <CaseFictionNotice />
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">HOST-ONLY · CASE BUILDER</div>
          <h1 className="font-cairo text-3xl text-white font-black mt-1">بناء القضايا المخصصة</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAI(true)} disabled={busy} data-testid="builder-new-ai"
                  className="btn-primary text-sm py-2 px-4 flex items-center gap-2 bg-gradient-to-r from-purple-900 to-purple-700 border-purple-500">
            <Sparkles size={14} /> إنشاء بالذكاء الاصطناعي
          </button>
          <button onClick={createBlank} disabled={busy} data-testid="builder-new" className="btn-primary text-sm py-2 px-4 flex items-center gap-2"><Plus size={14} /> إنشاء يدوي</button>
        </div>
      </div>

      {showAI && <AICaseBuilderModal onClose={() => setShowAI(false)} onDone={(caseId) => { setShowAI(false); load(); nav(`/host/case-builder/${caseId}`); }} />}

      {/* Template gallery */}
      <div className="panel border-metal p-4 mb-5">
        <div className="label-caps mb-2">إنشاء من قالب قضية موجودة</div>
        <div className="text-xs text-zinc-500 mb-3">اختر قضية جاهزة كنقطة انطلاق، ثم عدّل القصة والأدلة كما تريد.</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {builtins.map(b => (
            <button key={b} onClick={() => dupBuiltin(b)} disabled={busy} data-testid={`builder-dup-${b}`}
              className="border border-metal hover:border-gold p-3 text-xs text-right transition-colors">
              <div className="text-gold font-mono-ar text-[10px] mb-1">{b.toUpperCase()}</div>
              <div className="font-cairo text-white">استخدم كقالب</div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom cases list */}
      <div className="space-y-2">
        <div className="label-caps mb-2">قضاياك المخصصة ({cases.length})</div>
        {cases.length === 0 && <div className="panel p-6 text-center text-zinc-500 text-sm">لا توجد قضايا مخصصة بعد. ابدأ من الصفر أو من قالب.</div>}
        {cases.map(c => (
          <div key={c.id} className={`panel p-4 flex items-center gap-3 border ${c.status === "published" ? "border-emerald-800" : c.status === "disabled" ? "border-red-900" : "border-metal"}`} data-testid={`builder-case-${c.id}`}>
            {c.cover ? <img src={assetUrl(c.cover)} alt="" className="w-16 h-16 object-cover border border-metal" /> : <div className="w-16 h-16 border border-metal bg-zinc-900 flex items-center justify-center text-zinc-600 text-[10px]">لا غلاف</div>}
            <div className="flex-1 min-w-0">
              <div className="flex gap-2 items-center flex-wrap">
                <span className="text-[9px] text-gold font-mono-ar">{c.code}</span>
                <span className={`text-[9px] font-mono-ar px-1.5 py-0.5 ${c.status === "published" ? "text-emerald-400 border border-emerald-800" : c.status === "disabled" ? "text-red-400 border border-red-800" : "text-zinc-400 border border-metal"}`}>
                  {c.status === "published" ? "منشورة" : c.status === "disabled" ? "معطلة" : "مسودة"}
                </span>
                <button
                  onClick={() => toggleLock(c.id)}
                  data-testid={`builder-lock-${c.id}`}
                  className={`text-[9px] font-cairo px-2 py-0.5 border rounded flex items-center gap-1 transition-all ${
                    c.is_locked
                      ? "bg-amber-950/80 border-amber-600 text-amber-300 hover:bg-amber-900"
                      : "bg-emerald-950/80 border-emerald-600 text-emerald-300 hover:bg-emerald-900"
                  }`}
                  title={c.is_locked ? "مقفلة — اضغط للإتاحة" : "متاحة الآن — اضغط للقفل"}
                >
                  {c.is_locked ? (
                    <>
                      <Lock size={10} className="text-amber-400" />
                      <span>🔒 مقفلة — قريباً</span>
                    </>
                  ) : (
                    <>
                      <Unlock size={10} className="text-emerald-400" />
                      <span>🔓 متاحة الآن</span>
                    </>
                  )}
                </button>
              </div>
              <div className="font-cairo text-lg text-white truncate">{c.title}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">مشتبه بهم: {c.suspects_count} · أدلة: {c.evidence_count} · كاميرات: {c.cctv_count}</div>
            </div>
            <div className="flex gap-1">
              <Link to={`/host/case-builder/${c.id}`} data-testid={`builder-edit-${c.id}`} className="btn-primary text-[11px] py-1.5 px-3 flex items-center gap-1"><Pencil size={11} /> تعديل</Link>
              <button onClick={() => dupCustom(c.id)} data-testid={`builder-copy-${c.id}`} title="نسخ" className="w-8 h-8 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><Copy size={12} /></button>
              {c.status !== "published" && <button onClick={() => publish(c.id)} data-testid={`builder-publish-${c.id}`} title="نشر" className="w-8 h-8 border border-emerald-800 hover:border-emerald-500 text-emerald-400 flex items-center justify-center"><Eye size={12} /></button>}
              {c.status === "published" && <button onClick={() => unpublish(c.id)} data-testid={`builder-unpublish-${c.id}`} title="إلغاء نشر (يعود مسودة)" className="w-8 h-8 border border-amber-800 hover:border-amber-500 text-amber-400 flex items-center justify-center"><EyeOff size={12} /></button>}
              {hasPerm("DELETE_CASE") && <button onClick={() => setConfirmDel(c.id)} data-testid={`builder-delete-${c.id}`} title="حذف" className="w-8 h-8 border border-red-800 hover:border-red-500 text-red-400 flex items-center justify-center"><Trash2 size={12} /></button>}
            </div>
          </div>
        ))}
      </div>

      {confirmDel && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overscroll-contain"
          dir="rtl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="builder-delete-modal-title"
          data-testid="builder-delete-confirm"
          onClick={() => { if (!deleting) setConfirmDel(null); }}
        >
          <div
            className="relative w-full max-w-md bg-[#141417] border border-red-900/60 rounded-lg p-6 shadow-2xl overflow-hidden font-cairo text-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-metal/40">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-red-950/80 border border-red-800/80 flex items-center justify-center shrink-0">
                  <AlertTriangle className="text-red-400" size={18} />
                </div>
                <div>
                  <h3 id="builder-delete-modal-title" className="text-lg font-bold text-white leading-tight font-cairo">
                    تأكيد الحذف
                  </h3>
                  {targetCase?.code && (
                    <span className="text-[11px] text-gold font-mono-ar">{targetCase.code}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                disabled={deleting}
                onClick={() => { if (!deleting) setConfirmDel(null); }}
                data-testid="builder-delete-confirm-close"
                className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-zinc-800/80 transition-colors cursor-pointer shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="إغلاق النافذة"
                title="إغلاق (Esc)"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="text-sm text-zinc-300 mb-6 leading-relaxed">
              <p>
                هل أنت متأكد من حذف القضية{" "}
                {targetCase?.title ? (
                  <strong className="text-white font-bold">«{targetCase.title}»</strong>
                ) : (
                  "هذه"
                )}{" "}
                نهائياً؟
              </p>
              <p className="text-xs text-red-400/90 mt-2 font-cairo">
                ⚠ لا يمكن التراجع عن هذا الإجراء بعد الحذف.
              </p>
            </div>

            {/* Buttons / Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-metal/40">
              <button
                type="button"
                disabled={deleting}
                onClick={() => { if (!deleting) setConfirmDel(null); }}
                data-testid="builder-delete-confirm-cancel"
                className="px-4 py-2 text-sm font-semibold rounded border border-zinc-600 bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200 hover:text-white transition-colors cursor-pointer shrink-0 min-w-[90px] text-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={() => del(confirmDel)}
                disabled={deleting}
                data-testid="builder-delete-confirm-yes"
                className="px-5 py-2 text-sm font-bold rounded bg-red-600 hover:bg-red-500 active:bg-red-700 text-white border border-red-500/80 shadow-md transition-colors cursor-pointer shrink-0 min-w-[120px] text-center flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={14} />
                <span>{deleting ? "جاري الحذف..." : "تأكيد الحذف"}</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Shell>
  );
}

// ============ editor route ============
export function CaseBuilderEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completingAI, setCompletingAI] = useState(false);
  const [section, setSection] = useState("basics");
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/host/custom-cases/${id}`);
      setC(data);
    } catch {
      setC(null);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (silent = false) => {
    if (!c) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/custom-cases/${id}`, c);
      setC(data);
      if (!silent) { toast.success("تم الحفظ"); sfx.notification(); }
    } catch (e) { toast.error("فشل الحفظ"); }
    finally { setSaving(false); }
  }, [c, id]);

  // autosave every 20s if dirty state exists (simple heuristic: after any change)
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (!dirty || !c) return; const t = setTimeout(() => { save(true); setDirty(false); }, 15000); return () => clearTimeout(t); }, [dirty, c, save]);
  const patch = (updater) => { setC(prev => { const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater }; return next; }); setDirty(true); };

  const publish = async () => {
    if (!window.confirm("نشر القضية؟ ستظهر للمستخدمين المسموح لهم فوراً بدون إعادة نشر الموقع.")) return;
    try {
      await save(true);
      await api.post(`/custom-cases/${id}/publish`);
      toast.success("✓ تم نشر القضية"); sfx.caseSolved(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل نشر القضية");
    }
  };
  const unpublish = async () => {
    if (!window.confirm("إلغاء نشر القضية؟ ستعود مسودة وتختفي من المستخدمين. لن تُحذَف بياناتها.")) return;
    try {
      await api.post(`/custom-cases/${id}/unpublish`);
      toast.success("تم إلغاء النشر — عادت للمسودة"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إلغاء النشر");
    }
  };

  const toggleLock = async () => {
    try {
      const { data } = await api.post(`/cases/${id}/toggle-lock`);
      setC(prev => ({ ...prev, is_locked: data.is_locked }));
      toast.success(data.is_locked ? "🔒 تم قفل القضية — تظهر للمستخدمين كـ 'قريباً'" : "🔓 تم فتح القضية وأصبحت متاحة للجميع!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تغيير حالة القفل");
    }
  };

  const completeWithAI = async () => {
    if (!id || completingAI) return;
    setCompletingAI(true);
    try {
      const { data } = await api.post(`/owner/ai/cases/${id}/regenerate`, { part: "complete_draft" });
      if (data?.case) setC(data.case);
      toast.success("تم استكمال المسودة بالذكاء الاصطناعي — راجعها قبل النشر");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر الاستكمال — المسودة محفوظة");
    } finally {
      setCompletingAI(false);
    }
  };

  if (!c) return <Shell><div className="text-zinc-500 p-8">جاري التحميل...</div></Shell>;

  return (
    <Shell>
      <CaseFictionNotice />
      {/* Sticky header */}
      <div className="flex items-center justify-between mb-4 sticky top-0 bg-[#0A0A0C]/90 backdrop-blur border-b border-metal py-2 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/host/case-builder")} className="w-8 h-8 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center" data-testid="builder-back"><ArrowRight size={14} /></button>
          <div>
            <div className="text-[9px] tracking-[0.35em] text-gold font-mono-ar">CASE BUILDER · {c.status?.toUpperCase()}</div>
            <input value={c.title || ""} onChange={(e) => patch({ title: e.target.value })}
              className="font-cairo text-2xl bg-transparent outline-none text-white w-full border-b border-transparent focus:border-gold"
              data-testid="builder-title" placeholder="عنوان القضية" />
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button
            type="button"
            onClick={toggleLock}
            data-testid="builder-editor-toggle-lock"
            className={`px-3 py-1.5 text-xs font-cairo flex items-center gap-1.5 border rounded transition-all ${
              c.is_locked
                ? "bg-amber-950/80 border-amber-600 text-amber-300 hover:bg-amber-900"
                : "bg-emerald-950/80 border-emerald-600 text-emerald-300 hover:bg-emerald-900"
            }`}
          >
            {c.is_locked ? (
              <>
                <Lock size={12} className="text-amber-400" />
                <span>حالة القضية: 🔒 مقفلة — قريباً</span>
              </>
            ) : (
              <>
                <Unlock size={12} className="text-emerald-400" />
                <span>حالة القضية: 🔓 متاحة الآن</span>
              </>
            )}
          </button>
          <button onClick={() => setPreview(true)} data-testid="builder-preview" className="btn-ghost text-xs flex items-center gap-1"><Eye size={12} /> معاينة القضية</button>
          {c.status !== "published" && <button onClick={completeWithAI} disabled={completingAI} data-testid="builder-ai-complete" className="btn-ghost text-xs flex items-center gap-1 text-purple-300 border-purple-800/60 disabled:opacity-40"><Sparkles size={12} /> {completingAI ? "جاري الاستكمال…" : "إكمال بالـ AI"}</button>}
          <button onClick={() => save()} disabled={saving} data-testid="builder-save" className="btn-primary text-xs flex items-center gap-1"><Save size={12} /> {saving ? "..." : "حفظ كمسودة"}</button>
          {c.status === "published" ? (
            <button onClick={unpublish} data-testid="builder-unpublish" className="btn-primary text-xs bg-amber-900 border-amber-600 flex items-center gap-1"><EyeOff size={12} /> إلغاء نشر</button>
          ) : (
            <button onClick={publish} data-testid="builder-publish" className="btn-primary text-xs bg-emerald-900 border-emerald-600 flex items-center gap-1"><Check size={12} /> نشر القضية</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-[220px_1fr] gap-4">
        {/* Section nav */}
        <div className="panel border-metal p-2 h-fit sticky top-16">
          {SECTIONS.map(s => (
            <button key={s.key} onClick={() => setSection(s.key)} data-testid={`builder-section-${s.key}`}
              className={`w-full text-right px-3 py-2 text-xs flex items-center gap-2 ${section === s.key ? "bg-[rgba(197,160,89,0.1)] border-r-2 border-gold text-gold" : "text-zinc-400 hover:text-white"}`}>
              <s.icon size={13} />
              <span className="font-cairo">{s.label}</span>
            </button>
          ))}
          <div className="border-t border-metal my-2"></div>
          <div className="px-3 py-2 text-[10px] text-zinc-500 font-mono-ar">ID: {c.id}</div>
        </div>

        {/* Section content */}
        <div className="min-w-0">
          {section === "basics" && <SectionBasics c={c} patch={patch} />}
          {section === "media" && <SectionMedia c={c} patch={patch} />}
          {section === "suspects" && <SectionSuspects c={c} patch={patch} />}
          {section === "evidence" && <EvidenceSection c={c} patch={patch} />}
          {section === "roster" && <SectionRoster c={c} patch={patch} />}
          {section === "cctv" && <SectionCCTV c={c} patch={patch} />}
          {section === "forensics" && <SectionForensics c={c} patch={patch} />}
          {section === "phone_docs" && <SectionPhoneDocs c={c} patch={patch} />}
          {section === "scene" && <SectionList c={c} patch={patch} keyName="crime_scene" title="عناصر مسرح الجريمة" fields={[{k:"name",l:"الاسم"},{k:"description",l:"الوصف",area:true},{k:"image",l:"صورة",media:true}]} idPrefix="SC" />}
          {section === "timeline" && <SectionTimeline c={c} patch={patch} />}
          {section === "interr" && <SectionInterrogation c={c} patch={patch} />}
          {section === "solution" && <SectionSolution c={c} patch={patch} />}
        </div>
      </div>

      {preview && <CasePreviewModal caseId={c.id} onClose={() => setPreview(false)} />}
    </Shell>
  );
}

// ============ shared media uploader ============
function MediaField({ label, value, onChange, testid, accept = "image/*" }) {
  const [uploading, setUploading] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  const fileInputRef = useRef(null);
  const inputId = testid || `media-${Math.random().toString(36).slice(2, 7)}`;
  const isImage = accept.startsWith("image");
  const buttonText = uploading ? "..." : (isImage ? "رفع صورة" : "رفع ملف");

  useEffect(() => { setImgErr(false); }, [value]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("تم رفع الملف");
    } catch (e) { toast.error("فشل الرفع"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };
  return (
    <div>
      <div className="text-[10px] text-zinc-500 font-mono-ar block mb-1">{label}</div>
      <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
        {value && (
          isImage ? (
            <div className="w-14 h-14 border border-metal shrink-0 bg-black/60 rounded overflow-hidden flex items-center justify-center">
              {!imgErr ? (
                <img src={assetUrl(value)} alt="" onError={() => setImgErr(true)} className="w-full h-full object-cover" />
              ) : (
                <Camera size={18} className="text-zinc-600" />
              )}
            </div>
          ) : (
            <div className="text-[10px] text-emerald-400 shrink-0">✔ ملف مرفوع</div>
          )
        )}
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
          data-testid={`${inputId}-url`}
          className="flex-1 min-w-[140px] bg-black/40 border border-metal px-2 py-1.5 text-xs text-white outline-none focus:border-gold"
          placeholder="/api/uploads/... أو /api/images/..." />
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={upload}
          style={{ display: "none" }}
          data-testid={`${inputId}-file`}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid={`${inputId}-upload-btn`}
          className="btn-ghost text-[11px] py-1.5 px-3 cursor-pointer flex items-center gap-1 shrink-0 whitespace-nowrap"
        >
          <Upload size={11} /> {buttonText}
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="text-red-400 hover:text-red-300 p-1 shrink-0" title="حذف"><X size={12} /></button>}
      </div>
    </div>
  );
}

// ============ Dedicated Suspect Portrait Card ============
function SuspectPortraitUpload({ suspect, index, update, caseId }) {
  const [uploading, setUploading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setImgError(false);
  }, [suspect.portrait]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(index, "portrait", data.url);
      update(index, "portrait_status", "READY");
      toast.success("تم رفع صورة المشتبه به بنجاح");
    } catch {
      toast.error("فشل رفع الصورة");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const regenPortrait = async () => {
    if (!caseId || caseId === "new") {
      toast.error("يرجى حفظ القضية أولاً لإعادة التوليد");
      return;
    }
    setRegenerating(true);
    try {
      const { data } = await api.post(`/owner/ai/cases/${caseId}/regenerate`, {
        part: "suspect_portrait",
        suspect_id: suspect.id,
      });
      if (data.ok && data.case) {
        const updatedSuspect = (data.case.suspects || []).find(s => s.id === suspect.id);
        if (updatedSuspect?.portrait) {
          update(index, "portrait", updatedSuspect.portrait);
          update(index, "portrait_status", updatedSuspect.portrait_status || "READY");
          toast.success("تم إعادة توليد الصورة بنجاح");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إعادة التوليد");
    } finally {
      setRegenerating(false);
    }
  };

  const hasImage = Boolean(suspect.portrait && !imgError);

  return (
    <div className="flex flex-col items-center bg-black/40 border border-metal/60 p-3 rounded space-y-2.5 w-full">
      <div className="text-[10px] text-zinc-400 font-mono-ar self-start">صورة المشتبه به</div>

      {/* Portrait Box */}
      <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded border border-metal/80 bg-zinc-950 flex items-center justify-center overflow-hidden group shadow-inner">
        {hasImage ? (
          <img
            src={assetUrl(suspect.portrait)}
            alt={suspect.name || "مشتبه به"}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-zinc-600 p-2 text-center">
            <User size={34} className="text-zinc-500 mb-1 opacity-60" />
            <span className="text-[10px] text-zinc-400 font-cairo">
              {imgError ? "تعذر تحميل الصورة" : "لا توجد صورة"}
            </span>
          </div>
        )}

        {suspect.portrait_status === "PENDING" && (
          <span className="absolute top-1 right-1 bg-amber-950/90 text-amber-300 border border-amber-600/60 text-[9px] px-1.5 py-0.5 rounded font-mono-ar">
            قيد التوليد
          </span>
        )}
      </div>

      {/* Buttons */}
      <div className="w-full space-y-1.5">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={upload}
          style={{ display: "none" }}
          data-testid={`s-portrait-${index}-file`}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid={`s-portrait-${index}-upload-btn`}
          className="w-full btn-primary text-xs py-1.5 px-2 flex items-center justify-center gap-1.5 font-cairo cursor-pointer"
        >
          <Upload size={12} />
          <span>{uploading ? "جاري الرفع..." : hasImage ? "استبدال الصورة" : "رفع صورة"}</span>
        </button>

        {caseId && (
          <button
            type="button"
            onClick={regenPortrait}
            disabled={regenerating}
            data-testid={`s-portrait-${index}-regen-btn`}
            className="w-full btn-ghost text-[11px] py-1 px-2 flex items-center justify-center gap-1 text-purple-300 border-purple-800/50 hover:border-purple-500"
          >
            <Sparkles size={11} />
            <span>{regenerating ? "جاري التوليد..." : "إعادة توليد AI"}</span>
          </button>
        )}

        <div className="flex items-center justify-between pt-1 text-[10px] text-zinc-500">
          <button
            type="button"
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="hover:text-gold transition-colors underline decoration-dotted"
          >
            {showUrlInput ? "إخفاء الرابط" : "تعديل الرابط"}
          </button>

          {suspect.portrait && (
            <button
              type="button"
              onClick={() => { update(index, "portrait", ""); update(index, "portrait_status", "NONE"); }}
              className="text-red-400 hover:text-red-300 flex items-center gap-0.5"
              title="حذف الصورة"
            >
              <Trash2 size={10} />
              <span>حذف</span>
            </button>
          )}
        </div>

        {showUrlInput && (
          <input
            value={suspect.portrait || ""}
            onChange={(e) => update(index, "portrait", e.target.value)}
            data-testid={`s-portrait-${index}-url`}
            placeholder="/api/images/... أو رابط خارجي"
            className="w-full bg-black/60 border border-metal px-2 py-1 text-[11px] text-zinc-200 outline-none focus:border-gold rounded font-mono-ar"
          />
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, area, options, className = "", testid }) {
  if (options) {
    return (
      <div className={className}>
        <label className="text-[10px] text-zinc-500 font-mono-ar block mb-1">{label}</label>
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full bg-black/40 border border-metal px-2 py-1.5 text-xs text-white outline-none focus:border-gold">
          <option value="">—</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  const Elem = area ? "textarea" : "input";
  return (
    <div className={className}>
      <label className="text-[10px] text-zinc-500 font-mono-ar block mb-1">{label}</label>
      <Elem value={value || ""} onChange={(e) => onChange(e.target.value)} rows={area ? 3 : undefined}
        data-testid={testid}
        className={`w-full bg-black/40 border border-metal px-2 py-1.5 text-xs text-white outline-none focus:border-gold ${area ? "resize-none" : ""}`} />
    </div>
  );
}

// ============ Section: Basics ============
function SectionBasics({ c, patch }) {
  return (
    <div className="panel p-5 space-y-4">
      {/* Lock status banner & toggle */}
      <div className={`p-4 border rounded flex items-center justify-between gap-3 ${c.is_locked ? "bg-amber-950/40 border-amber-600/70" : "bg-zinc-900/60 border-zinc-800"}`}>
        <div>
          <div className="text-sm font-cairo font-bold text-white flex items-center gap-2">
            {c.is_locked ? <Lock size={16} className="text-amber-400" /> : <Unlock size={16} className="text-emerald-400" />}
            <span>حالة القضية: {c.is_locked ? "🔒 مقفلة — قريباً" : "🔓 متاحة الآن"}</span>
          </div>
          <div className="text-xs text-zinc-400 mt-1">
            {c.is_locked
              ? "القضية تظهر في الموقع مع شارة 'قريباً' مع منع الدخول والتحقيق للمستخدمين."
              : "القضية مفتوحة ومتاحة لجميع المحققين للبدء بالتحقيق وحل اللغز."}
          </div>
        </div>
        <button
          type="button"
          onClick={() => patch({ is_locked: !c.is_locked })}
          data-testid="basics-toggle-lock"
          className={`px-4 py-2 text-xs font-cairo font-bold border rounded transition-all shrink-0 ${
            c.is_locked
              ? "bg-amber-500 hover:bg-amber-400 text-black border-amber-400"
              : "bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border-zinc-700"
          }`}
        >
          {c.is_locked ? "🔓 إتاحة القضية الآن" : "🔒 قفل القضية (قريباً)"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field testid="b-code" label="رمز القضية (Code)" value={c.code} onChange={(v) => patch({ code: v })} />
        <Field testid="b-difficulty" label="الصعوبة" value={c.difficulty} onChange={(v) => patch({ difficulty: v })} options={["سهلة","متوسطة","صعبة","خبير"]} />
        <Field testid="b-status" label="الحالة" value={c.status} onChange={(v) => patch({ status: v })} options={["draft","published","disabled"]} />
        <Field testid="b-required-plan" label="الحد الأدنى للباقة" value={c.required_plan || "FREE"} onChange={(v) => patch({ required_plan: v })} options={["FREE","VIP","LEGENDARY","ROYAL","STREAMER"]} />
      </div>
      <Field testid="b-description" label="القصة / الوصف" value={c.description} onChange={(v) => patch({ description: v })} area />
      <div className="grid grid-cols-3 gap-3">
        <Field testid="b-location" label="المكان" value={c.location} onChange={(v) => patch({ location: v })} />
        <Field testid="b-date" label="التاريخ" value={c.date} onChange={(v) => patch({ date: v })} />
        <Field testid="b-time" label="الوقت" value={c.time} onChange={(v) => patch({ time: v })} />
      </div>
    </div>
  );
}

// ============ Section: Media ============
function SectionMedia({ c, patch }) {
  return (
    <div className="panel p-5 space-y-4">
      <MediaField testid="m-cover" label="غلاف القضية (4K)" value={c.cover} onChange={(v) => patch({ cover: v })} />
      <MediaField testid="m-scene" label="صورة مسرح الجريمة" value={c.scene} onChange={(v) => patch({ scene: v })} />
    </div>
  );
}

// ============ Section: Suspects ============
function SectionSuspects({ c, patch }) {
  const list = c.suspects || [];
  const add = () => patch(p => ({ ...p, suspects: [...(p.suspects || []), { id: uid("SUS"), name: "مشتبه جديد", age: "", job: "", relation: "", motive: "", secret: "", portrait: "", alibi: { claim: "", status: "unverified" } }] }));
  const del = (i) => patch(p => ({ ...p, suspects: p.suspects.filter((_, j) => j !== i) }));
  const update = (i, field, v) => patch(p => ({ ...p, suspects: p.suspects.map((s, j) => j === i ? { ...s, [field]: v } : s) }));
  const updateAlibi = (i, field, v) => patch(p => ({ ...p, suspects: p.suspects.map((s, j) => j === i ? { ...s, alibi: { ...(s.alibi || {}), [field]: v } } : s) }));
  const move = (i, dir) => patch(p => { const arr = [...p.suspects]; const j = i + dir; if (j < 0 || j >= arr.length) return p; [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...p, suspects: arr }; });
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="label-caps">المشتبه بهم ({list.length})</div>
        <button onClick={add} data-testid="suspect-add" className="btn-primary text-xs flex items-center gap-1"><Plus size={12} /> إضافة مشتبه به</button>
      </div>
      {list.map((s, i) => (
        <div key={s.id} className="panel border-metal p-4" data-testid={`suspect-row-${i}`}>
          <div className="flex justify-between items-center mb-3">
            <span className="text-[10px] text-gold font-mono-ar">{s.id}</span>
            <div className="flex gap-1">
              <button onClick={() => move(i, -1)} className="text-zinc-500 hover:text-gold p-1"><ArrowRight size={12} /></button>
              <button onClick={() => move(i, 1)} className="text-zinc-500 hover:text-gold p-1"><ArrowLeft size={12} /></button>
              <button onClick={() => del(i)} data-testid={`suspect-del-${i}`} className="text-red-500 hover:text-red-300 p-1"><Trash2 size={12} /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4 items-start">
            <SuspectPortraitUpload suspect={s} index={i} update={update} caseId={c?.id} />
            <div className="grid grid-cols-2 gap-3">
              <Field testid={`s-name-${i}`} label="الاسم" value={s.name} onChange={(v) => update(i, "name", v)} />
              <Field testid={`s-age-${i}`} label="العمر" value={s.age} onChange={(v) => update(i, "age", v)} />
              <Field testid={`s-job-${i}`} label="المهنة / الدور" value={s.job} onChange={(v) => update(i, "job", v)} />
              <Field testid={`s-relation-${i}`} label="صلة القرابة / العلاقة" value={s.relation} onChange={(v) => update(i, "relation", v)} />
              <Field testid={`s-motive-${i}`} label="الدافع المحتمل" value={s.motive} onChange={(v) => update(i, "motive", v)} className="col-span-2" area />
              <Field testid={`s-secret-${i}`} label="سر خفي (خاص بالمضيف)" value={s.secret} onChange={(v) => update(i, "secret", v)} className="col-span-2" area />
              <Field testid={`s-alibi-claim-${i}`} label="ادعاء العذر" value={s.alibi?.claim} onChange={(v) => updateAlibi(i, "claim", v)} />
              <Field testid={`s-alibi-status-${i}`} label="حالة العذر" value={s.alibi?.status} onChange={(v) => updateAlibi(i, "status", v)} options={["verified","unverified","broken"]} />
            </div>
          </div>
        </div>
      ))}
      {list.length === 0 && <div className="panel p-6 text-center text-zinc-500 text-sm">لا يوجد مشتبه بهم. أضف واحداً على الأقل.</div>}
    </div>
  );
}

// ============ Section: Evidence (with PIN toggle for Squad Mode) ============
function EvidencePinRow({ caseId, ev, i, upd }) {
  const [busy, setBusy] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [pin, setPin] = useState("");
  const [hint, setHint] = useState(ev.pin_hint || "");
  const isLocked = !!ev.pin_locked;

  const apply = async ({ remove = false } = {}) => {
    if (!caseId || caseId === "new") { toast.error("احفظ القضية أولاً قبل تعيين رمز الدخول"); return; }
    const body = remove
      ? { remove: true }
      : { pin: pin.trim(), pin_hint: hint.trim() };
    if (!remove) {
      if (!body.pin) { toast.error("أدخل رمز الدخول"); return; }
      if (body.pin.length < 3 || body.pin.length > 12) { toast.error("الرمز بين 3 و 12 محرفاً"); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/owner/cases/${caseId}/evidence/${ev.id}/pin`, body);
      const eNew = data?.evidence || {};
      upd(i, "pin_locked", !!eNew.pin_locked);
      upd(i, "pin_hint", eNew.pin_hint || "");
      toast.success(remove ? "تم إلغاء القفل" : "تم حفظ رمز الدخول");
      setShowEditor(false);
      setPin("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "فشل الحفظ");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 border-t border-metal/40 pt-2" data-testid={`evidence-pin-row-${i}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] text-zinc-400">قفل الدليل برمز (لفرقة التحقيق)</div>
        <div className="flex items-center gap-2">
          {isLocked ? (
            <span className="text-[10px] text-amber-400 font-mono-ar" data-testid={`evidence-pin-locked-badge-${i}`}>مقفول برمز</span>
          ) : (
            <span className="text-[10px] text-zinc-500">مفتوح</span>
          )}
          <button
            onClick={() => setShowEditor(!showEditor)}
            data-testid={`evidence-pin-toggle-${i}`}
            className="text-[10px] text-gold border border-gold/50 px-2 py-0.5 hover:bg-[rgba(197,160,89,0.1)]"
          >{isLocked ? "تغيير الرمز" : "تعيين رمز"}</button>
          {isLocked && (
            <button
              onClick={() => apply({ remove: true })}
              disabled={busy}
              data-testid={`evidence-pin-remove-${i}`}
              className="text-[10px] text-red-400 border border-red-500/40 px-2 py-0.5 hover:bg-red-500/10 disabled:opacity-40"
            >إلغاء القفل</button>
          )}
        </div>
      </div>
      {showEditor && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
          <label className="text-[10px] text-zinc-400 block">
            الرمز (3-12 محارف)
            <input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value.slice(0, 12))}
              data-testid={`evidence-pin-value-${i}`}
              placeholder={isLocked ? "أدخل رمز جديد" : "1234"}
              className="w-full mt-1 bg-black/40 border border-metal px-2 py-1 text-sm text-white outline-none focus:border-gold font-mono-ar tracking-widest"
            />
          </label>
          <label className="text-[10px] text-zinc-400 block">
            تلميح (اختياري) — يظهر للاعبين قبل الفتح
            <input
              type="text"
              value={hint}
              onChange={(e) => setHint(e.target.value.slice(0, 120))}
              data-testid={`evidence-pin-hint-${i}`}
              placeholder="مثال: رقم غرفة الفندق"
              className="w-full mt-1 bg-black/40 border border-metal px-2 py-1 text-sm text-white outline-none focus:border-gold"
            />
          </label>
          <div className="md:col-span-2 text-[10px] text-zinc-500">
            المحتوى (الوصف/الصورة) الحالي سيبقى ظاهراً في وضع اللعب العادي.
            في «فرقة التحقيق»، سيُخفى المحتوى ولن يظهر إلا بعد إدخال الرمز الصحيح.
          </div>
          <button
            onClick={() => apply()}
            disabled={busy || !pin.trim()}
            data-testid={`evidence-pin-save-${i}`}
            className="md:col-span-2 btn-primary text-xs disabled:opacity-40"
          >{busy ? "..." : "حفظ رمز الدخول"}</button>
        </div>
      )}
    </div>
  );
}

function EvidenceSection({ c, patch }) {
  const caseId = c.id;
  const [regenId, setRegenId] = useState(null);
  const fields = [
    {k:"name",l:"الاسم"},
    {k:"importance",l:"الأهمية",options:["منخفضة","متوسطة","حاسمة"]},
    {k:"description",l:"الوصف",area:true},
    {k:"image",l:"صورة الدليل",media:true},
  ];
  const list = c.evidence || [];
  const add = () => patch(p => ({ ...p, evidence: [...(p.evidence || []), Object.fromEntries([["id", uid("EV")], ...fields.map(f => [f.k, ""])])] }));
  const del = (i) => patch(p => ({ ...p, evidence: p.evidence.filter((_, j) => j !== i) }));
  const upd = (i, k, v) => patch(p => ({ ...p, evidence: p.evidence.map((x, j) => j === i ? { ...x, [k]: v } : x) }));

  const regenImage = async (evId, idx) => {
    if (!caseId || caseId === "new") {
      toast.error("احفظ القضية أولاً لإعادة التوليد");
      return;
    }
    setRegenId(evId);
    try {
      const { data } = await api.post(`/owner/ai/cases/${caseId}/regenerate`, {
        part: "evidence_image",
        evidence_id: evId,
      });
      if (data.ok && data.case) {
        const updated = (data.case.evidence || []).find(e => e.id === evId);
        if (updated?.image) {
          upd(idx, "image", updated.image);
          upd(idx, "image_status", "READY");
          toast.success("تم توليد صورة الدليل بنجاح");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل توليد صورة الدليل");
    } finally {
      setRegenId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="label-caps">الأدلة ({list.length})</div>
        <button onClick={add} data-testid="evidence-add" className="btn-primary text-xs flex items-center gap-1"><Plus size={12} /> إضافة</button>
      </div>
      {list.map((item, i) => (
        <div key={item.id || i} className="panel border-metal p-3" data-testid={`evidence-row-${i}`}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-[10px] text-gold font-mono-ar">{item.id}</span>
            <div className="flex items-center gap-2">
              {caseId && (
                <button
                  type="button"
                  onClick={() => regenImage(item.id, i)}
                  disabled={regenId === item.id}
                  data-testid={`evidence-regen-btn-${i}`}
                  className="text-[10px] text-purple-300 border border-purple-800/60 px-2 py-0.5 hover:bg-purple-900/20 flex items-center gap-1 rounded transition-colors"
                >
                  <Sparkles size={10} />
                  <span>{regenId === item.id ? "جاري التوليد..." : "توليد صورة AI"}</span>
                </button>
              )}
              <button onClick={() => del(i)} data-testid={`evidence-del-${i}`} className="text-red-500 hover:text-red-300 p-1"><Trash2 size={11} /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {fields.map(f => f.media
              ? (
                <div key={f.k} className="md:col-span-2 space-y-1">
                  <MediaField testid={`evidence-${f.k}-${i}`} label={f.l} value={item[f.k]} onChange={(v) => { upd(i, f.k, v); upd(i, "image_status", v ? "READY" : "NONE"); }} />
                  {item.image_status === "PENDING" && (
                    <div className="text-[10px] text-amber-400 font-mono-ar">⏳ الصورة قيد التوليد أو لم تكتمل بعد</div>
                  )}
                </div>
              )
              : <Field key={f.k} testid={`evidence-${f.k}-${i}`} label={f.l} value={item[f.k]} onChange={(v) => upd(i, f.k, v)} area={f.area} options={f.options} className={f.area ? "md:col-span-2" : ""} />
            )}
          </div>
          <EvidencePinRow caseId={caseId} ev={item} i={i} upd={upd} />
        </div>
      ))}
    </div>
  );
}

// ============ Section: Generic List (evidence, timeline, scene) ============
function SectionList({ c, patch, keyName, title, fields, idPrefix }) {
  const list = c[keyName] || [];
  const add = () => patch(p => ({ ...p, [keyName]: [...(p[keyName] || []), Object.fromEntries([["id", uid(idPrefix)], ...fields.map(f => [f.k, ""])])] }));
  const del = (i) => patch(p => ({ ...p, [keyName]: p[keyName].filter((_, j) => j !== i) }));
  const upd = (i, k, v) => patch(p => ({ ...p, [keyName]: p[keyName].map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="label-caps">{title} ({list.length})</div>
        <button onClick={add} data-testid={`${keyName}-add`} className="btn-primary text-xs flex items-center gap-1"><Plus size={12} /> إضافة</button>
      </div>
      {list.map((item, i) => (
        <div key={item.id || i} className="panel border-metal p-3" data-testid={`${keyName}-row-${i}`}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-[10px] text-gold font-mono-ar">{item.id}</span>
            <button onClick={() => del(i)} data-testid={`${keyName}-del-${i}`} className="text-red-500 hover:text-red-300 p-1"><Trash2 size={11} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {fields.map(f => f.media
              ? <MediaField key={f.k} testid={`${keyName}-${f.k}-${i}`} label={f.l} value={item[f.k]} onChange={(v) => upd(i, f.k, v)} />
              : <Field key={f.k} testid={`${keyName}-${f.k}-${i}`} label={f.l} value={item[f.k]} onChange={(v) => upd(i, f.k, v)} area={f.area} options={f.options} className={f.area ? "md:col-span-2" : ""} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ Section: CCTV ============
function SectionCCTV({ c, patch }) {
  const cams = c.cctv_cameras || [];
  const add = () => patch(p => ({ ...p, cctv_cameras: [...(p.cctv_cameras || []), { id: uid("CAM"), name: "كاميرا جديدة", location: "", resolution: "1080p", duration: 300, events: [] }] }));
  const del = (i) => patch(p => ({ ...p, cctv_cameras: p.cctv_cameras.filter((_, j) => j !== i) }));
  const upd = (i, k, v) => patch(p => ({ ...p, cctv_cameras: p.cctv_cameras.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const addEvent = (i) => upd(i, "events", [...(cams[i].events || []), { t: 0, time: "", label: "", tag: "routine", suspect_id: "" }]);
  const delEvent = (i, k) => upd(i, "events", cams[i].events.filter((_, j) => j !== k));
  const updEvent = (i, k, field, v) => upd(i, "events", cams[i].events.map((e, j) => j === k ? { ...e, [field]: field === "t" ? Number(v) : v } : e));
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="label-caps">كاميرات المراقبة ({cams.length})</div>
        <button onClick={add} data-testid="cctv-add" className="btn-primary text-xs flex items-center gap-1"><Plus size={12} /> إضافة كاميرا</button>
      </div>
      {cams.map((cam, i) => (
        <div key={cam.id} className="panel border-metal p-3" data-testid={`cctv-cam-row-${i}`}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-[10px] text-gold font-mono-ar">{cam.id}</span>
            <button onClick={() => del(i)} data-testid={`cctv-cam-del-${i}`} className="text-red-500 hover:text-red-300 p-1"><Trash2 size={11} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field testid={`cctv-name-${i}`} label="اسم الكاميرا" value={cam.name} onChange={(v) => upd(i, "name", v)} />
            <Field testid={`cctv-location-${i}`} label="الموقع" value={cam.location} onChange={(v) => upd(i, "location", v)} />
            <Field testid={`cctv-resolution-${i}`} label="الدقة" value={cam.resolution} onChange={(v) => upd(i, "resolution", v)} options={["480p","720p","1080p","4K"]} />
            <Field testid={`cctv-duration-${i}`} label="المدة (ث)" value={cam.duration} onChange={(v) => upd(i, "duration", Number(v) || 0)} />
          </div>
          <div className="mt-3">
            <div className="flex justify-between items-center mb-2">
              <div className="text-[10px] text-zinc-500 font-mono-ar">الأحداث ({(cam.events || []).length})</div>
              <button onClick={() => addEvent(i)} data-testid={`cctv-event-add-${i}`} className="text-[10px] text-gold border border-gold px-2 py-0.5 hover:bg-[rgba(197,160,89,0.1)]">+ حدث</button>
            </div>
            {(cam.events || []).map((ev, k) => (
              <div key={k} className="grid grid-cols-[80px_80px_1fr_120px_120px_30px] gap-1 mb-1 items-center" data-testid={`cctv-event-row-${i}-${k}`}>
                <input value={ev.t} onChange={(e) => updEvent(i, k, "t", e.target.value)} placeholder="ثانية" className="bg-black/40 border border-metal px-1 py-1 text-xs text-white outline-none focus:border-gold" />
                <input value={ev.time} onChange={(e) => updEvent(i, k, "time", e.target.value)} placeholder="HH:MM" className="bg-black/40 border border-metal px-1 py-1 text-xs text-white outline-none focus:border-gold" />
                <input value={ev.label} onChange={(e) => updEvent(i, k, "label", e.target.value)} placeholder="وصف الحدث" className="bg-black/40 border border-metal px-1 py-1 text-xs text-white outline-none focus:border-gold" />
                <select value={ev.tag || "routine"} onChange={(e) => updEvent(i, k, "tag", e.target.value)} className="bg-black/40 border border-metal px-1 py-1 text-xs text-white outline-none focus:border-gold">
                  <option value="routine">اعتيادي</option><option value="suspicious">مشبوه</option><option value="critical">حاسم</option>
                </select>
                <input value={ev.suspect_id || ""} onChange={(e) => updEvent(i, k, "suspect_id", e.target.value)} placeholder="ID المشتبه (اختياري)" className="bg-black/40 border border-metal px-1 py-1 text-xs text-white outline-none focus:border-gold" />
                <button onClick={() => delEvent(i, k)} className="text-red-500 hover:text-red-300"><X size={11} /></button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ Section: Timeline ============
// Each event names the characters present by ID, so the integrity validator
// can tell a legitimate mention from an invented one.
function SectionTimeline({ c, patch }) {
  const roster = [
    ...(c.victim?.name ? [{ id: c.victim.id || "victim_01", name: c.victim.name }] : []),
    ...(c.suspects || []).map(s => ({ id: s.id, name: s.name })),
    ...(c.npcs || []).map(n => ({ id: n.id, name: n.name })),
  ];
  const nameOf = (id) => roster.find(r => String(r.id) === String(id))?.name || id;
  const toggleActor = (i, id) => patch(p => ({
    ...p,
    timeline: (p.timeline || []).map((t, j) => {
      if (j !== i) return t;
      const cur = t.actor_ids || [];
      return { ...t, actor_ids: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    }),
  }));

  return (
    <div className="space-y-4" data-testid="section-timeline">
      <SectionList
        c={c} patch={patch} keyName="timeline" title="أحداث الجدول الزمني"
        fields={[{ k: "at", l: "التوقيت" }, { k: "event", l: "الحدث", area: true }, { k: "source", l: "المصدر" }]}
        idPrefix="T"
      />
      {(c.timeline || []).length > 0 && roster.length > 0 && (
        <div className="space-y-2">
          <div className="label-caps">الشخصيات الحاضرة في كل حدث</div>
          {(c.timeline || []).map((t, i) => (
            <div key={t.id || i} className="panel border-metal p-2.5" data-testid={`timeline-actors-${i}`}>
              <div className="text-[11px] text-zinc-400 mb-1.5">
                <span className="text-gold font-mono-ar">{t.at || "—"}</span> · {(t.event || "").slice(0, 70)}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {roster.map(r => {
                  const on = (t.actor_ids || []).includes(r.id);
                  return (
                    <button key={r.id} onClick={() => toggleActor(i, r.id)} type="button"
                      data-testid={`timeline-actor-${i}-${r.id}`}
                      className={`px-2 py-0.5 text-[10px] rounded border transition-all ${on ? "border-gold text-gold bg-[rgba(197,160,89,0.12)]" : "border-metal text-zinc-500 hover:border-gold/50"}`}>
                      {nameOf(r.id)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ Section: Character Registry (victim + suspects + NPCs) ============
// The single roster every other section must draw from. The victim and the
// secondary cast previously had nowhere to appear in this editor, so a
// perfectly legitimate mention of either read to the owner as a character
// that "does not exist" in the case.
function SectionRoster({ c, patch }) {
  const victim = c.victim || {};
  const suspects = c.suspects || [];
  const npcs = c.npcs || [];
  const culpritId = String(c.solution?.culprit_id || c.solution?.culprit || "");
  const updVictim = (k, v) => patch(p => ({ ...p, victim: { ...(p.victim || {}), [k]: v } }));

  return (
    <div className="space-y-6" data-testid="section-roster">
      <div className="panel border-metal p-3 text-[11px] text-zinc-400 leading-relaxed">
        هذه هي كل الشخصيات المعتمدة في القضية. أي اسم يظهر في الخط الزمني أو الأدلة أو
        الاستجوابات أو الكاميرات أو الوثائق ولا يوجد هنا يُعتبر خللاً ويمنعه المدقق.
      </div>

      {/* Victim */}
      <div className="space-y-3">
        <div className="label-caps">الضحية</div>
        <div className="panel border-metal p-3 grid grid-cols-2 gap-2" data-testid="roster-victim">
          <Field testid="roster-victim-name" label="الاسم" value={victim.name || ""} onChange={(v) => updVictim("name", v)} />
          <Field testid="roster-victim-age" label="العمر" value={victim.age || ""} onChange={(v) => updVictim("age", Number(v) || 0)} />
          <Field testid="roster-victim-job" label="المهنة" value={victim.job || ""} onChange={(v) => updVictim("job", v)} />
          <Field testid="roster-victim-gender" label="الجنس" value={victim.gender || "male"} onChange={(v) => updVictim("gender", v)} options={["male", "female"]} />
          <div className="col-span-2">
            <Field testid="roster-victim-background" label="الخلفية" value={victim.background || ""} onChange={(v) => updVictim("background", v)} area />
          </div>
          <div className="col-span-2">
            <Field testid="roster-victim-moves" label="آخر تحركات" value={victim.last_movements || ""} onChange={(v) => updVictim("last_movements", v)} area />
          </div>
          {victim.id && <div className="col-span-2 text-[10px] text-zinc-600 font-mono-ar">{victim.id}</div>}
        </div>
      </div>

      {/* Suspects — read-only here; edited in the suspects tab */}
      <div className="space-y-2">
        <div className="label-caps">المشتبه بهم ({suspects.length})</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {suspects.map(sp => (
            <div key={sp.id} className="panel border-metal p-2.5 flex items-center justify-between" data-testid={`roster-suspect-${sp.id}`}>
              <div>
                <div className="text-sm text-white font-cairo">
                  {sp.name}
                  {String(sp.id) === culpritId && <span className="text-[9px] text-red-400 mr-2">الجاني</span>}
                </div>
                <div className="text-[10px] text-zinc-500">{sp.job || "—"}{sp.age ? ` · ${sp.age}` : ""}</div>
              </div>
              <div className="text-[10px] text-zinc-600 font-mono-ar">{sp.id}</div>
            </div>
          ))}
          {suspects.length === 0 && <div className="text-xs text-zinc-500">لا يوجد مشتبه بهم بعد — أضفهم من تبويب «المشتبه بهم».</div>}
        </div>
      </div>

      {/* Secondary cast */}
      <SectionList
        c={c} patch={patch} keyName="npcs" title="شخصيات ثانوية (غير مشتبه بهم)"
        fields={[
          { k: "name", l: "الاسم" },
          { k: "role", l: "الدور في القضية" },
          { k: "gender", l: "الجنس", options: ["male", "female"] },
          { k: "description", l: "الوصف", area: true },
        ]}
        idPrefix="npc"
      />
      <div className="text-[11px] text-zinc-500">
        عرّف هنا أي شخص ستذكره القصة دون أن يكون مشتبهاً به (حارس، طبيب، شاهد…). تعريفه هنا
        هو ما يجعل ذكره في باقي الأقسام مقبولاً لدى المدقق.
      </div>
    </div>
  );
}

// ============ Section: Forensics (Fingerprints + DNA + Audio + Materials) ============
function SectionForensics({ c, patch }) {
  return (
    <div className="space-y-6">
      <SectionList c={c} patch={patch} keyName="fingerprints" title="عينات البصمات" fields={[{k:"sample_id",l:"معرّف العينة"},{k:"source",l:"المصدر"},{k:"matched_to",l:"مطابق للمشتبه (ID)"}]} idPrefix="FP" />
      <SectionList c={c} patch={patch} keyName="dna" title="عينات الحمض النووي" fields={[{k:"sample_id",l:"معرّف العينة"},{k:"source",l:"المصدر"},{k:"matched_to",l:"مطابق للمشتبه (ID)"}]} idPrefix="DNA" />
      <SectionList c={c} patch={patch} keyName="audio" title="أدلة صوتية" fields={[{k:"name",l:"الاسم"},{k:"description",l:"الوصف",area:true},{k:"url",l:"ملف صوتي",media:true},{k:"matched_to",l:"مطابق للمشتبه (اختياري)"}]} idPrefix="AUD" />
      <SectionList c={c} patch={patch} keyName="materials" title="مواد وعينات" fields={[{k:"name",l:"الاسم"},{k:"description",l:"الوصف",area:true},{k:"image",l:"صورة",media:true}]} idPrefix="MAT" />
    </div>
  );
}

// ============ Section: Phone + Documents ============
function SectionPhoneDocs({ c, patch }) {
  return (
    <div className="space-y-6">
      <SectionList c={c} patch={patch} keyName="documents" title="الوثائق" fields={[{k:"name",l:"الاسم"},{k:"description",l:"الوصف",area:true},{k:"image",l:"صورة الوثيقة",media:true}]} idPrefix="DOC" />
      {/* owner/contact are the displayed names; the *_id fields are the canon
          link the integrity validator follows. */}
      <SectionList c={c} patch={patch} keyName="phone" title="سجلات الهاتف" fields={[{k:"owner",l:"المالك"},{k:"owner_id",l:"ID المالك (اختياري)"},{k:"contact",l:"الطرف الآخر"},{k:"contact_id",l:"ID الطرف الآخر (اختياري)"},{k:"kind",l:"النوع",options:["مكالمة","رسالة","صورة","بريد"]},{k:"content",l:"المحتوى",area:true},{k:"at",l:"التوقيت"}]} idPrefix="PH" />
    </div>
  );
}

// ============ Section: Interrogation ============
function SectionInterrogation({ c, patch }) {
  const suspects = c.suspects || [];
  const [selected, setSelected] = useState(suspects[0]?.id || "");
  useEffect(() => { if (suspects[0]?.id && !selected) setSelected(suspects[0].id); }, [suspects, selected]);
  const qs = (c.interrogations || {})[selected] || [];
  const upd = (fn) => patch(p => ({ ...p, interrogations: { ...(p.interrogations || {}), [selected]: fn(p.interrogations?.[selected] || []) } }));
  const add = () => upd(cur => [...cur, { q: "", a: "", reaction: "calm", linked: [] }]);
  const del = (i) => upd(cur => cur.filter((_, j) => j !== i));
  const setField = (i, k, v) => upd(cur => cur.map((x, j) => j === i ? { ...x, [k]: v } : x));
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="label-caps">استجواب المشتبه بهم</div>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="interr-suspect-select"
          className="bg-black/40 border border-metal px-2 py-1.5 text-xs text-white outline-none focus:border-gold">
          <option value="">— اختر مشتبهاً به —</option>
          {suspects.map(s => <option key={s.id} value={s.id}>{s.id} — {s.name}</option>)}
        </select>
      </div>
      {selected && (
        <>
          <button onClick={add} data-testid="interr-add" className="btn-primary text-xs flex items-center gap-1"><Plus size={12} /> إضافة سؤال</button>
          {qs.map((q, i) => (
            <div key={i} className="panel border-metal p-3" data-testid={`interr-q-${i}`}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-[10px] text-gold font-mono-ar">Q{String(i + 1).padStart(2, "0")}</span>
                <button onClick={() => del(i)} className="text-red-500 hover:text-red-300 p-1"><Trash2 size={11} /></button>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <Field testid={`interr-q-text-${i}`} label="السؤال" value={q.q} onChange={(v) => setField(i, "q", v)} area />
                <Field testid={`interr-a-text-${i}`} label="الإجابة" value={q.a} onChange={(v) => setField(i, "a", v)} area />
                <Field testid={`interr-reaction-${i}`} label="رد الفعل" value={q.reaction} onChange={(v) => setField(i, "reaction", v)}
                  options={["calm","nervous","defiant","contradiction"]} />
                <Field testid={`interr-linked-${i}`} label="أدلة مرتبطة (ID مفصولة بفواصل)" value={(q.linked || []).join(", ")} onChange={(v) => setField(i, "linked", v.split(",").map(x => x.trim()).filter(Boolean))} />
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ============ Section: Solution ============
function SectionSolution({ c, patch }) {
  const sol = c.solution || {};
  const setSol = (k, v) => patch(p => ({ ...p, solution: { ...(p.solution || {}), [k]: v } }));
  const suspects = c.suspects || [];
  return (
    <div className="space-y-3">
      <div className="panel border-red-800 p-3 bg-red-950/20 flex items-center gap-2">
        <AlertTriangle className="text-red-400 shrink-0" size={16} />
        <div className="text-[11px] text-red-300">هذا القسم للمضيف فقط. لا يُعرض للمشاهدين أو على Overlay البث بأي شكل.</div>
      </div>
      <div className="panel border-metal p-4 space-y-3">
        <Field testid="sol-culprit" label="الجاني الحقيقي" value={sol.culprit} onChange={(v) => setSol("culprit", v)} options={["", ...suspects.map(s => s.id)]} />
        <Field testid="sol-accomplice" label="الشريك (اختياري)" value={sol.accomplice} onChange={(v) => setSol("accomplice", v)} options={["", ...suspects.map(s => s.id)]} />
        <Field testid="sol-motive" label="الدافع" value={sol.motive} onChange={(v) => setSol("motive", v)} area />
        <Field testid="sol-method" label="الأسلوب" value={sol.method} onChange={(v) => setSol("method", v)} area />
        <Field testid="sol-explanation" label="الشرح الكامل" value={sol.explanation} onChange={(v) => setSol("explanation", v)} area />
        <Field testid="sol-evidence" label="أدلة إدانة (IDs مفصولة بفواصل)" value={(sol.evidence_ids || []).join(", ")} onChange={(v) => setSol("evidence_ids", v.split(",").map(x => x.trim()).filter(Boolean))} />
        <Field testid="sol-red-herrings" label="المشتبه بهم المضللون (IDs مفصولة بفواصل)" value={(sol.red_herrings || []).join(", ")} onChange={(v) => setSol("red_herrings", v.split(",").map(x => x.trim()).filter(Boolean))} />
      </div>
    </div>
  );
}

// ============ Preview modal ============
function CasePreviewModal({ caseId, onClose }) {
  return (
    <div className="fixed inset-0 z-[900] bg-black/95 backdrop-blur flex flex-col p-4" dir="rtl" data-testid="builder-preview-modal">
      <div className="flex justify-between items-center mb-2">
        <div>
          <div className="text-[9px] tracking-[0.4em] text-gold font-mono-ar">CASE PREVIEW · AUDIENCE VIEW</div>
          <div className="font-cairo text-xl text-white">معاينة القضية كما يراها المشاهدون</div>
        </div>
        <button onClick={onClose} data-testid="builder-preview-close" className="btn-ghost text-xs">إغلاق</button>
      </div>
      <iframe src={`/cases/${caseId}?preview=1`} title="preview" className="flex-1 w-full border border-metal bg-black" />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// AI CASE BUILDER MODAL (P6)
// ─────────────────────────────────────────────────────────────
function AICaseBuilderModal({ onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [opts, setOpts] = useState({
    kind: "", location: "", suspects_count: "", difficulty: "", on_short_cast: "fail",
    duration_min: "", mood: "", twist: false, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [stages, setStages] = useState([]);
  const [stageStatus, setStageStatus] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Provider self-check: a failed build names the stage that stopped, not the
  // reason the provider gave. This asks the provider directly.
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/owner/ai/cases/stages");
        setStages(data.stages || []);
      } catch {}
    })();
  }, []);

  // Keep the page behind the dialog inert while the modal is open. The
  // cleanup restores any previous page scroll setting when it closes.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  const checkProvider = async () => {
    setChecking(true); setHealth(null);
    try {
      const { data } = await api.get("/owner/ai/llm/health");
      setHealth(data);
      if (data?.ok) toast.success("مزوّد الذكاء الاصطناعي يعمل");
      else toast.error("مزوّد الذكاء الاصطناعي لا يستجيب");
    } catch (e) {
      setHealth({
        ok: false,
        message: e?.response?.data?.detail || e?.message || "تعذّر تنفيذ الفحص",
      });
      toast.error("تعذّر تنفيذ الفحص");
    } finally {
      setChecking(false);
    }
  };

  const run = async () => {
    if (!title.trim()) { toast.error("أدخل اسم/فكرة القضية أولاً"); return; }
    setBusy(true); setError(null); setResult(null);
    const initial = {}; stages.forEach(s => { initial[s.id] = "pending"; });
    if (Object.keys(initial).length) initial[stages[0].id] = "running";
    setStageStatus(initial);
    try {
      const payload = { title: title.trim(), options: {}, with_media: true };
      Object.entries(opts).forEach(([k, v]) => {
        if (v === "" || v === false || v === null || v === undefined) return;
        payload.options[k] = (k === "suspects_count" || k === "duration_min") ? Number(v) : v;
      });
      // Kick off async job — server returns immediately with job_id.
      const { data: startResp } = await api.post("/owner/ai/cases/generate", payload);
      const jobId = startResp.job_id;
      // Poll job status every 3s for up to 8 minutes.
      // Staged generation makes ~6 sequential LLM calls, not 1.
      const deadline = Date.now() + 12 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) throw new Error("انتهت مهلة الانتظار — تحقق من السجلات");
        await new Promise((r) => setTimeout(r, 3000));
        const { data: st } = await api.get(`/owner/ai/cases/jobs/${jobId}`);
        setStageStatus(st.stages || {});
        if (st.status === "done") {
          setResult({
            case_id: st.case_id,
            validation: st.validation,
            validation_report: st.validation_report,
            media_report: st.media_report,
            roster: st.roster,
            requested_suspects: st.requested_suspects,
            warnings: st.warnings || [],
            llm_calls: st.llm_calls,
            media_calls_attempted: st.media_calls_attempted,
          });
          toast.success("✓ تم حفظ مسودة القضية بنجاح");
          break;
        }
        if (st.status === "failed") throw new Error(st.error || "فشل مجهول");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "فشل بناء القضية");
      toast.error("فشل بناء القضية");
    } finally { setBusy(false); }
  };

  const completeDraft = async () => {
    if (!result?.case_id || completing) return;
    setCompleting(true);
    try {
      const { data } = await api.post(`/owner/ai/cases/${result.case_id}/regenerate`, {
        part: "complete_draft",
      });
      if (data?.ok) {
        toast.success("تم استكمال المسودة بالذكاء الاصطناعي — راجعها قبل النشر");
        onDone(result.case_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذر استكمال المسودة — بقيت محفوظة");
    } finally {
      setCompleting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[2000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overscroll-contain" dir="rtl" role="dialog" aria-modal="true" data-testid="ai-case-modal">
      <div className="relative w-full max-w-2xl bg-neutral-950 border border-purple-800/50 rounded-lg shadow-2xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] overflow-hidden flex flex-col min-h-0">
        <header className="p-5 border-b border-neutral-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-900/40 border border-purple-500/40 flex items-center justify-center">
              <Sparkles size={18} className="text-purple-300"/>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] text-purple-400 font-mono-ar">AI CASE BUILDER</div>
              <h2 className="text-lg font-cairo font-black text-white">إنشاء قضية بالذكاء الاصطناعي</h2>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="text-neutral-400 hover:text-white p-1" data-testid="ai-modal-close"><X size={18}/></button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-5 space-y-4 font-cairo">
          {!result && (
            <>
              <div>
                <label className="text-[11px] text-neutral-400 block mb-1.5">اسم / فكرة القضية <span className="text-red-400">*</span></label>
                <input value={title} onChange={e => setTitle(e.target.value)}
                       placeholder="مثال: سرقة في متحف الرياض"
                       data-testid="ai-title-input" autoFocus
                       className="w-full bg-black/60 border border-neutral-800 rounded px-4 py-3 text-base font-black text-white"/>
                <div className="text-[10px] text-neutral-500 mt-1">
                  يكفي كتابة الفكرة — سيبني الذكاء الاصطناعي القصة والمشتبه بهم والأدلة والاستجوابات والحل تلقائياً.
                </div>
              </div>

              <button onClick={() => setShowAdvanced(!showAdvanced)}
                      data-testid="ai-toggle-advanced"
                      className="text-xs text-purple-300 inline-flex items-center gap-1 hover:text-purple-200">
                <ChevronDown size={12} className={showAdvanced ? "rotate-180 transition" : "transition"}/>
                خيارات متقدمة — اختياري
              </button>

              {showAdvanced && (
                <div className="grid grid-cols-2 gap-3 p-3 border border-neutral-800 rounded bg-black/30" data-testid="ai-advanced-panel">
                  <FieldSmall label="نوع القضية" value={opts.kind}
                              onChange={v => setOpts({ ...opts, kind: v })}
                              placeholder="سرقة / قتل / اختطاف …"/>
                  <FieldSmall label="المكان" value={opts.location}
                              onChange={v => setOpts({ ...opts, location: v })}
                              placeholder="فندق / صحراء …"/>
                  <FieldSmallSelect label="عدد المشتبه بهم" value={opts.suspects_count}
                              onChange={v => setOpts({ ...opts, suspects_count: v })}
                              options={["", "3", "4", "5", "6", "7", "8"]}
                              display={{ "": "AI يقرر (4)" }}/>
                  <FieldSmallSelect label="عند نقص المشتبه بهم" value={opts.on_short_cast}
                              onChange={v => setOpts({ ...opts, on_short_cast: v })}
                              options={["fail", "save"]}
                              display={{ fail: "أوقف البناء", save: "احفظ مسودة بما نجح" }}/>
                  <FieldSmallSelect label="الصعوبة" value={opts.difficulty}
                              onChange={v => setOpts({ ...opts, difficulty: v })}
                              options={["", "easy", "medium", "hard"]}
                              display={{ "": "AI يقرر", easy: "سهلة", medium: "متوسطة", hard: "صعبة" }}/>
                  <FieldSmall label="مدة اللعب (دقيقة)" value={opts.duration_min}
                              onChange={v => setOpts({ ...opts, duration_min: v })}
                              type="number" placeholder="30-90"/>
                  <FieldSmall label="الأجواء" value={opts.mood}
                              onChange={v => setOpts({ ...opts, mood: v })}
                              placeholder="غامضة / مرعبة / كلاسيكية"/>
                  <label className="col-span-2 flex items-center gap-2 text-[11px] text-neutral-300 cursor-pointer">
                    <input type="checkbox" checked={opts.twist}
                           onChange={e => setOpts({ ...opts, twist: e.target.checked })}
                           data-testid="ai-opt-twist"/>
                    مطلوب: نهاية ملتوية غير متوقعة
                  </label>
                  <div className="col-span-2">
                    <label className="text-[10px] text-neutral-500 block mb-1">ملاحظات إضافية</label>
                    <textarea value={opts.notes} onChange={e => setOpts({ ...opts, notes: e.target.value })}
                              rows={2} className="w-full bg-black/60 border border-neutral-800 rounded px-3 py-2 text-xs"/>
                  </div>
                </div>
              )}

              {stages.length > 0 && Object.keys(stageStatus).length > 0 && (
                <div className="space-y-1.5 pt-3 border-t border-neutral-800" data-testid="ai-stages">
                  {stages.map(s => {
                    const st = stageStatus[s.id];
                    const icon = st === "done" ? "✓" : st === "running" ? "…" : st === "failed" ? "✕" : "○";
                    const color = st === "done" ? "text-emerald-400"
                                : st === "running" ? "text-yellow-300 animate-pulse"
                                : st === "failed" ? "text-red-400"
                                : "text-neutral-600";
                    return (
                      <div key={s.id} className={`flex items-center gap-2 text-xs font-cairo ${color}`}>
                        <span className="w-4 text-center font-mono">{icon}</span> {s.label}
                      </div>
                    );
                  })}
                </div>
              )}

              {error && (
                <div className="border border-red-800/50 bg-red-950/20 rounded p-3 text-xs text-red-300">
                  <div className="font-black mb-1">لم يكتمل البناء</div>
                  <div className="opacity-80">{error}</div>
                  {/* A provider fault is not a bad case idea — saying so stops
                      the owner from rewriting the prompt over and over. */}
                  {/llm_output_truncated/.test(String(error)) ? (
                    <div className="mt-2 pt-2 border-t border-amber-800/40 opacity-90" data-testid="ai-error-truncation-hint">
                      خرجت استجابة الذكاء عن حد التوكنات. تم رفع الميزانية ومحاولة إكمال الشخصيات تلقائياً؛ يمكنك اختيار «حفظ مسودة بما نجح» أو المحاولة مرة أخرى.
                    </div>
                  ) : /llm_|empty_llm_response/.test(String(error)) && (
                    <div className="mt-2 pt-2 border-t border-red-800/40 opacity-90" data-testid="ai-error-provider-hint">
                      هذا عطل في مزوّد الذكاء الاصطناعي، لا في فكرة القضية — تغيير الفكرة أو
                      الإعدادات لن يُصلحه. راجع مفتاح المزوّد ورصيده في إعدادات الخادم، ثم أعد المحاولة.
                    </div>
                  )}
                </div>
              )}

              {health && (
                <div className={`rounded p-3 text-xs border ${health.ok
                  ? "border-emerald-800/50 bg-emerald-950/20 text-emerald-300"
                  : "border-red-800/50 bg-red-950/20 text-red-300"}`}
                     data-testid="ai-provider-health">
                  <div className="font-black mb-1">
                    {health.ok ? "فحص المزوّد: يعمل" : "فحص المزوّد: لا يستجيب"}
                  </div>
                  <div className="opacity-80">{health.message}</div>
                  {/* The key itself is never returned — only enough of a
                      fingerprint to tell two deployed keys apart. */}
                  <div className="mt-2 pt-2 border-t border-white/10 opacity-70 font-mono text-[10px] leading-relaxed" dir="ltr">
                    {[
                      health.kind ? `kind=${health.kind}` : null,
                      health.http_status ? `http=${health.http_status}` : null,
                      health.host ? `host=${health.host}` : null,
                      health.model ? `model=${health.model}` : null,
                      (health.models_tried && health.models_tried.length > 1)
                        ? `tried=${health.models_tried.join(" → ")}` : null,
                      health.key_configured === false ? "key=missing"
                        : (health.key_fingerprint ? `key=${health.key_fingerprint}` : null),
                    ].filter(Boolean).join("  |  ")}
                  </div>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="space-y-4" data-testid="ai-result-panel">
              <div className={`border rounded p-4 ${
                result.media_report?.has_failures || (result.validation_report && !result.validation_report.valid)
                  ? "border-amber-700/60 bg-amber-950/25"
                  : "border-emerald-800/50 bg-emerald-950/20"
              }`}>
                <div className={`font-black mb-2 flex items-center gap-2 text-base ${
                  result.media_report?.has_failures || (result.validation_report && !result.validation_report.valid)
                    ? "text-amber-300"
                    : "text-emerald-300"
                }`}>
                  {result.media_report?.has_failures || (result.validation_report && !result.validation_report.valid) ? (
                    <>
                      <AlertTriangle size={18} className="text-amber-400 shrink-0" />
                      <span>تم حفظ المسودة — بعض العناصر بحاجة لمراجعتك أو استكمالها</span>
                    </>
                  ) : (
                    <>
                      <Check size={18} className="text-emerald-400 shrink-0" />
                      <span>✓ تم إنشاء المسودة بنجاح وكافة الوسائط مكتملة</span>
                    </>
                  )}
                </div>

                <div className="text-xs text-neutral-300 space-y-2 mt-3">
                  {/* Detailed Media Status Grid */}
                  {result.media_report && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-black/50 p-3 rounded border border-neutral-800 text-[11px]">
                      <div>
                        <span className="text-zinc-500 block text-[10px]">صورة الغلاف</span>
                        <span className={result.media_report.cover === "READY" ? "text-emerald-400 font-bold" : "text-amber-400 font-bold"}>
                          {result.media_report.cover === "READY" ? "جاهزة ✓" : "قيد الرفع اليدوي"}
                        </span>
                      </div>
                      <div>
                        <span className="text-zinc-500 block text-[10px]">صور المشتبه بهم</span>
                        <span className={result.media_report.suspects_ready === result.media_report.suspects_total ? "text-emerald-400 font-bold" : "text-amber-400 font-bold"}>
                          {result.media_report.suspects_ready} من {result.media_report.suspects_total} جاهزة
                        </span>
                      </div>
                      <div>
                        <span className="text-zinc-500 block text-[10px]">صور الأدلة</span>
                        <span className={result.media_report.evidence_ready === result.media_report.evidence_total ? "text-emerald-400 font-bold" : "text-amber-400 font-bold"}>
                          {result.media_report.evidence_ready} من {result.media_report.evidence_total} جاهزة
                        </span>
                      </div>
                      <div>
                        <span className="text-zinc-500 block text-[10px]">المختبر الجنائي</span>
                        <span className="text-purple-300 font-bold">
                          {result.media_report.forensics_total > 0 ? `${result.media_report.forensics_total} عينات مُنشأة` : "لم تتطلب القصة"}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Frozen character registry — every name the case may use */}
                  {result.roster && (
                    <div className="bg-black/50 p-3 rounded border border-neutral-800 text-[11px] space-y-2" data-testid="ai-roster">
                      <div className="text-zinc-500 text-[10px]">سجل الشخصيات المعتمد — لا يجوز ظهور أي اسم خارجه</div>
                      {result.roster.victim?.name && (
                        <div>
                          <span className="text-zinc-500 text-[10px]">الضحية: </span>
                          <span className="text-rose-300 font-bold">{result.roster.victim.name}</span>
                          <span className="text-zinc-600 font-mono text-[9px]"> · {result.roster.victim.id}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        <span className="text-zinc-500 text-[10px] self-center">
                          المشتبه بهم ({(result.roster.suspects || []).length}
                          {result.requested_suspects ? ` من ${result.requested_suspects} مطلوبة` : ""}):
                        </span>
                        {(result.roster.suspects || []).map(sp => (
                          <span key={sp.id} className="px-2 py-0.5 rounded border border-neutral-700 bg-neutral-900/70 text-zinc-200">
                            {sp.name} <span className="text-zinc-600 font-mono text-[9px]">{sp.id}</span>
                          </span>
                        ))}
                      </div>
                      {(result.roster.npcs || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          <span className="text-zinc-500 text-[10px] self-center">شخصيات ثانوية:</span>
                          {result.roster.npcs.map(n => (
                            <span key={n.id} className="px-2 py-0.5 rounded border border-neutral-800 bg-neutral-900/50 text-zinc-400">
                              {n.name}{n.role ? ` — ${n.role}` : ""} <span className="text-zinc-600 font-mono text-[9px]">{n.id}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Integrity audit — a real per-check score, never a flat 7/7 */}
                  {result.validation_report && (
                    <div className="pt-1 border-t border-neutral-800/60 space-y-1.5" data-testid="ai-integrity-checks">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-zinc-400">فحص تكامل القضية</span>
                        <strong className={result.validation_report.valid ? "text-emerald-300" : "text-red-300"}>
                          {result.validation_report.checks_passed} / {result.validation_report.checks_total}
                          {result.validation_report.valid ? " ✓" : " — القضية تحتاج إصلاحاً"}
                        </strong>
                      </div>
                      <ul className="space-y-0.5">
                        {(result.validation_report.checks || []).map(chk => (
                          <li key={chk.id} className={`text-[10.5px] ${chk.passed ? "text-emerald-400/80" : "text-red-300"}`}>
                            <span className="font-mono">{chk.passed ? "✓" : "✕"}</span> {chk.label_ar}
                            {!chk.passed && (chk.errors || []).map((e, k) => (
                              <div key={k} className="pr-5 text-red-300/85">— {e}</div>
                            ))}
                          </li>
                        ))}
                      </ul>
                      <div className="text-[10px] text-zinc-500 pt-1">
                        رأي المدقق السردي (استشاري فقط، لا يغيّر النتيجة أعلاه):{" "}
                        <span className={result.validation?.playable ? "text-emerald-400/80" : "text-amber-400/80"}>
                          {result.validation?.playable ? "قابلة للحل منطقياً" : "تحتاج مراجعة"}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* What the pipeline had to repair on its own */}
                  {(result.validation_report?.repaired || []).length > 0 && (
                    <div className="bg-sky-950/30 border border-sky-900/50 p-2.5 rounded text-[11px] text-sky-300/90 space-y-1" data-testid="ai-repairs">
                      <div className="font-bold text-sky-400">أُصلحت تلقائياً قبل الحفظ:</div>
                      <ul className="list-disc pr-4 space-y-0.5">
                        {result.validation_report.repaired.map((r, k) => <li key={k}>{r}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* Generation warnings */}
                  {(result.warnings || []).length > 0 && (
                    <div className="bg-amber-950/30 border border-amber-900/50 p-2.5 rounded text-[11px] text-amber-300/90 space-y-1">
                      <div className="font-bold text-amber-400">ملاحظات التوليد:</div>
                      <ul className="list-disc pr-4 space-y-0.5">
                        {result.warnings.map((w, k) => <li key={k}>{w}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="text-[11px] text-zinc-400 bg-neutral-900/50 p-2 rounded border border-neutral-800/50">
                    💡 <strong>الخيارات اليدوية متاحة دائماً:</strong> يمكنك الآن مراجعة المسودة وتعديل أي نص أو رفع واستبدال أي صورة يدوياً بضغطة زر.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <footer className="shrink-0 p-4 border-t border-neutral-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {!result ? (
            <>
              <div className="flex items-center gap-3">
                <div className="text-[10px] text-neutral-500 font-cairo">
                  القضية تُحفَظ كمسودة — لن يراها أحد حتى تنشرها.
                </div>
                {/* One request to the same key, proxy and model the builder
                    uses — so a provider fault is named in seconds instead of
                    after a six-call generation run. */}
                <button onClick={checkProvider} disabled={busy || checking}
                        data-testid="ai-check-provider-btn"
                        className="text-[10px] text-neutral-400 hover:text-purple-300 underline underline-offset-2 disabled:opacity-40 shrink-0 font-cairo">
                  {checking ? "جاري الفحص…" : "فحص مزوّد الذكاء الاصطناعي"}
                </button>
              </div>
              <button onClick={run} disabled={busy || !title.trim()}
                      data-testid="ai-generate-btn"
                      className="btn-primary py-2.5 px-5 flex items-center gap-2 bg-gradient-to-r from-purple-800 to-purple-600 border-purple-500 disabled:opacity-40">
                <Sparkles size={14}/> {busy ? "جاري البناء…" : "بناء القضية كاملة"}
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="btn-ghost text-xs">إغلاق</button>
              <div className="flex items-center gap-2">
                <button onClick={completeDraft} disabled={completing}
                        data-testid="ai-complete-draft-btn"
                        className="btn-ghost py-2 px-3 text-xs flex items-center gap-2 text-purple-300 border-purple-800/60 disabled:opacity-40">
                  <Sparkles size={13}/> {completing ? "جاري الاستكمال…" : "إكمال بالذكاء الاصطناعي"}
                </button>
                <button onClick={() => onDone(result.case_id)}
                      data-testid="ai-open-case-btn"
                      className="btn-primary py-2 px-4 flex items-center gap-2 bg-emerald-800 border-emerald-600">
                  مراجعة القضية <ArrowLeft size={13}/>
                </button>
              </div>
            </>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
}

function FieldSmall({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-[10px] text-neutral-500 block mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
             placeholder={placeholder}
             className="w-full bg-black/60 border border-neutral-800 rounded px-3 py-2 text-xs"/>
    </div>
  );
}
function FieldSmallSelect({ label, value, onChange, options, display }) {
  return (
    <div>
      <label className="text-[10px] text-neutral-500 block mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
              className="w-full bg-black/60 border border-neutral-800 rounded px-3 py-2 text-xs">
        {options.map(o => <option key={o} value={o}>{display?.[o] ?? o}</option>)}
      </select>
    </div>
  );
}
